// resolveFlightTime — pure selection of the best time for a flight leg.
//
// Extracted verbatim from `FlightLookup.js` (module-level `resolveFlightTime`) as
// part of the behavior-preserving refactor (issue #240). When the leg is delayed
// the estimated time takes priority; otherwise scheduled wins. Each path falls
// back through formatted → raw-estimated → raw-scheduled fields, then normalizes
// the chosen value to a UTC ISO string.
//
// Totality preserved exactly: delegates to `storeAsUTC`, which returns `null` for
// a falsy resolved value, matching the source. No guards beyond the source's.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import hasDelay from './hasDelay.js';
import storeAsUTC from './storeAsUTC.js';

/**
 * Resolves the best available time for a flight leg (departure or arrival).
 *
 * @param {Object} flight - Raw flight data from FlightAware.
 * @param {'departure'|'arrival'} type - Which leg to resolve.
 * @returns {string|null} UTC ISO string, or `null`.
 */
export function resolveFlightTime(flight, type) {
  const isDeparture = type === 'departure';
  const formatted  = isDeparture ? flight.departure : flight.arrival;
  const estimated  = isDeparture ? flight.estimated_out : flight.estimated_in;
  const scheduled  = isDeparture ? flight.scheduled_out : flight.scheduled_in;

  const delayed = hasDelay(estimated, scheduled);

  const rawTime = delayed
    ? (formatted?.estimated || estimated || scheduled)
    : (formatted?.scheduled || scheduled || estimated);

  return storeAsUTC(rawTime);
}

export default resolveFlightTime;
